import { Arrange, Container, Icon, Spacer, SvgClose, Text } from '@loomhq/lens';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import {
  ROLLOUT_CHAT_CREATE,
  ROLLOUT_DESTINATION_PROFILE_BASIC,
} from 'constants/featureFlag';
import { CONTACT_SUPPORT_MODAL } from 'constants/modalTypes';
import { WHATSNEW } from 'constants/routes';
import {
  HELP_BUBBLE_CONTACT_SUPPORT,
  HELP_BUBBLE_EDU_LINK,
} from 'constants/scopes';

import useScript, { USE_SCRIPT_STATUS } from 'hooks/useScript';
import { useGetSelectedWorkspace } from 'hooks/workspace';
import { hasScope } from 'selectors/scopes';

import { CHATCREATE_BOT_ID, CHATCREATE_SESSION_STORAGE_KEY } from './constants';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';

import './styles.less';

const WHATS_NEW_LINK = {
  emoji: '📚',
  label: 'What’s new',
  url: WHATSNEW,
};
const HELP_CENTER_LINK = {
  emoji: '📚',
  label: 'Help center',
  url: 'https://support.loom.com/hc/en-us',
};
const WAYS_TO_USE_LOOM_LINK = {
  emoji: '🤓',
  label: 'Ways to use Loom',
  url: 'https://www.loom.com/use-case',
};
const TROUBLESHOOTING_LINK = {
  emoji: '🛠',
  label: 'Troubleshooting',
  url: 'https://support.loom.com/hc/en-us/articles/360002248118-How-to-quickly-fix-common-recording-errors',
};
const EDUCATIONAL_RESOURCES_LINK = {
  emoji: '🎓',
  label: 'Educational resources',
  url: 'https://www.loom.com/education',
};
const CAREERS_LINK = {
  emoji: '🎉',
  label: 'Loom is hiring!',
  url: 'https://www.loom.com/careers',
  isHighlighted: true,
};

const HelpMenuRow = ({ emoji, label, url, isHighlighted = false }) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="helpMenuRow"
    >
      <Spacer x="medium" y="small">
        <Arrange gap="small">
          {emoji}
          {isHighlighted ? (
            <Text color="orangeDark" fontWeight="bold">
              {label}
            </Text>
          ) : (
            <Text>{label}</Text>
          )}
        </Arrange>
      </Spacer>
    </a>
  );
};

const ChatRow = ({ setShowMenu }) => (
  <button
    className="helpMenuRow"
    onClick={() => {
      if (window.ccShowWidget) {
        window.ccShowWidget();
        setShowMenu(false);
      }
    }}
  >
    <Spacer x="medium" y="small">
      <Arrange gap="small">
        🤖
        <div>Chat</div>
      </Arrange>
    </Spacer>
  </button>
);

const ContactSupportRow = ({ setShowMenu, chatId }) => {
  const dispatch = useDispatch();

  return (
    <button
      className="helpMenuRow"
      onClick={() => {
        dispatch(openModal(CONTACT_SUPPORT_MODAL, { chatId }));
        setShowMenu(false);
      }}
    >
      <Spacer x="medium" y="small">
        <Arrange gap="small">
          💬
          <div>Contact support</div>
        </Arrange>
      </Spacer>
    </button>
  );
};

const HelpMenu = ({ show = false, setShowMenu, chatId }) => {
  const updatesLinks = [WHATS_NEW_LINK, CAREERS_LINK];
  const infoLinks = [
    HELP_CENTER_LINK,
    WAYS_TO_USE_LOOM_LINK,
    TROUBLESHOOTING_LINK,
  ];

  const helpLinks = [
    HELP_CENTER_LINK,
    WAYS_TO_USE_LOOM_LINK,
    TROUBLESHOOTING_LINK,
    CAREERS_LINK,
  ];

  const hasEduLinkScope = useSelector(state =>
    hasScope(state, HELP_BUBBLE_EDU_LINK)
  );

  if (hasEduLinkScope) {
    helpLinks.splice(3, 0, EDUCATIONAL_RESOURCES_LINK);
    infoLinks.splice(2, 0, EDUCATIONAL_RESOURCES_LINK);
  }

  const hasContactSupportScope = useSelector(state =>
    hasScope(state, HELP_BUBBLE_CONTACT_SUPPORT)
  );

  return (
    <>
      {show && (
        <Container
          backgroundColor="background"
          borderSide="all"
          radius="medium"
          shadow="large"
          paddingY="small"
          minWidth="15rem"
        >
          <FeatureFlaggedComponent
            flag={ROLLOUT_DESTINATION_PROFILE_BASIC}
            activationValues={[false]}
          >
            {helpLinks.map((link, i) => (
              <HelpMenuRow {...link} key={i} />
            ))}
            <FeatureFlaggedComponent
              flag={ROLLOUT_CHAT_CREATE}
              activationValues={[true]}
            >
              <>
                <div className="borderTop width:full my:small" />
                <ChatRow setShowMenu={setShowMenu} />
              </>
            </FeatureFlaggedComponent>
            {hasContactSupportScope && (
              <ContactSupportRow chatId={chatId} setShowMenu={setShowMenu} />
            )}
          </FeatureFlaggedComponent>
          <FeatureFlaggedComponent
            flag={ROLLOUT_DESTINATION_PROFILE_BASIC}
            activationValues={[true]}
          >
            {updatesLinks.map((link, i) => (
              <HelpMenuRow {...link} key={i} />
            ))}
            <div className="borderTop width:full my:small" />
            {infoLinks.map((link, i) => (
              <HelpMenuRow {...link} key={i} />
            ))}
            <FeatureFlaggedComponent
              flag={ROLLOUT_CHAT_CREATE}
              activationValues={[true]}
            >
              <>
                <div className="borderTop width:full my:small" />
                <ChatRow setShowMenu={setShowMenu} />
              </>
            </FeatureFlaggedComponent>
            {hasContactSupportScope && (
              <ContactSupportRow chatId={chatId} setShowMenu={setShowMenu} />
            )}
          </FeatureFlaggedComponent>
        </Container>
      )}
    </>
  );
};

const HelpBubble = () => {
  const { type: workspaceType } = useGetSelectedWorkspace();
  const [showMenu, setShowMenu] = useState(false);
  const [chatId, setChatId] = useState();
  const dispatch = useDispatch();
  const chatCreateStatus = useScript(
    `https://chatcreate.com/api/bots/${CHATCREATE_BOT_ID}/standalone/snippet`,
    { id: 'cc-snippet', defer: true }
  );

  useEffect(() => {
    const setStateFromEvent = e => {
      if (e?.data?.event?.value?.label?.startsWith('Email support')) {
        dispatch(openModal(CONTACT_SUPPORT_MODAL, { chatId }));

        setChatId(window.sessionStorage[CHATCREATE_SESSION_STORAGE_KEY]);
      }
    };

    if (
      chatCreateStatus === USE_SCRIPT_STATUS.READY &&
      window.ccSetParameter &&
      workspaceType
    ) {
      window.ccSetParameter('type', workspaceType);

      if (window.addEventListener) {
        window.addEventListener('message', setStateFromEvent, false);
      } else if (window.attachEvent) {
        window.attachEvent('onmessage', setStateFromEvent, false);
      }
    }
  }, [chatId, chatCreateStatus, workspaceType, dispatch]);

  const buttonIcon = showMenu ? (
    <Icon icon={<SvgClose />} color="grey8" />
  ) : (
    <Text size="xlarge" color="grey8">
      ?
    </Text>
  );

  if (chatCreateStatus !== USE_SCRIPT_STATUS.READY) {
    return null;
  }

  return (
    <div className="help">
      <Spacer top="medium">
        <button
          className={classNames(
            'helpBubble border flex items:center justify:center',
            { active: showMenu }
          )}
          onClick={() => setShowMenu(!showMenu)}
        >
          {buttonIcon}
        </button>
      </Spacer>
      <HelpMenu show={showMenu} setShowMenu={setShowMenu} chatId={chatId} />
    </div>
  );
};

export default HelpBubble;
